import {Injectable} from '@angular/core';
import * as moment from 'moment';


/**
 * Сервис для работы с датами.
 * Работает с датами в формате, заданном с this.dateFormat.
 * За основу взята библиотека moment https://momentjs.com/
 *
 * Многие методы возвращают DateService, чтобы можно было использовать их как цепочку.
 * Пример 1:
 * -> DateService.for('22.05.2017').addMonth(3).dateString
 * -> Прибавит к дате 22.05.2017 три месяца и вернет строку в таком же формате 22.08.2017
 * Пример 2:
 * -> DateService.addMonth(3).dateString
 * -> Прибавит к сегодняшней дате три месяца и вернет строку в таком же формате
 * Пример 3:
 * -> DateService.for(32.13.2017).isValid()
 * -> Распарсит дату 32.13.2017 и скажет, валидная она или нет.
 */

@Injectable()
export class DateService {

    /**
     * Возвращает текущую дату из this.moment в виде строки нужного формата
     * @returns {string}
     */
    public get dateString(): string {
        return this.moment.format(this.dateFormat);
    }

    static readonly format = 'DD.MM.YYYY';
    static readonly monthFormat = 'MM.YYYY';

    /**
     * Длина одного дня в милисекундах
     * Милисекунды * 60 секунд * 60 минут * 24 часа = 1 сутки
     */
    static oneDayTime = 1000 * 60 * 60 * 24;



    /**
     * Формат даты, с которым будем работать
     * @type {string}
     */
    private dateFormat = 'DD.MM.YYYY';

    /**
     * Объект moment.js с датой.
     * По-умолчанию содержит сегодняшнюю дату
     * @type {moment.Moment}
     */
    private moment = moment(new Date(), this.dateFormat);

    /**
     * Преобразовать timestamp в дату выбранного формата
     * @param timestamp
     */
    static timestampToFormat(timestamp: number|string): string {
        const stamp = this.normalizeTimestamp(timestamp);
        const date = new Date(stamp);
        return this.format.replace('DD', this.getDay(date))
            .replace('MM', this.getMonth(date))
            .replace('YYYY', this.getYear(date));
    }

    static timestampToDate(timestamp: number|string): Date {
        const stamp = this.normalizeTimestamp(timestamp);
        return new Date(stamp);
    }

    static dateToFormat(date: Date, isMonthFormat = false): string {
        const format = isMonthFormat ? this.monthFormat : this.format;
        return format.replace('DD', this.getDay(date))
            .replace('MM', this.getMonth(date))
            .replace('YYYY', this.getYear(date));
    }

    /**
     * Преобразует строковое значение даты, заданное в формате, в объект Date соответствующей даты
     * @param value
     */
    static formatedValueToDate(value: string): Date {
        if (value.length !== this.format.length) {
            return null;
        }

        const dIndex = this.format.indexOf('DD');
        const mIndex = this.format.indexOf('MM');
        const yIndex = this.format.indexOf('YYYY');

        const dValue = dIndex >= 0 ? Number(value.substr(dIndex, 2)) : null;
        const mValue = mIndex >= 0 ? Number(value.substr(mIndex, 2)) : null;
        const yValue = yIndex >= 0 ? Number(value.substr(yIndex, 4)) : null;

        if (!dValue || !mValue || !yValue) {
            return null;
        }

        return new Date(yValue, mValue - 1, dValue);
    }

    /**
     * Актуальная ли дата окончания.
     * Дата актуальна, если она еще не наступила
     * @param dateString
     */
    static isDateEndActual(dateString: string) {
        /**
         * Сравниваем даты как строки, реверсируя даты в формате DD.MM.YYYY в формат YYYY.MM.DD
         */
        const currentFormatDateString = this.dateToFormat(new Date());
        return dateString.split('.').reverse().join('.') >= currentFormatDateString.split('.').reverse().join('.');
    }

    /**
     * Нормализовать timestamp, переданный в секундах, до милисекунд
     * @param timestamp
     */
    static normalizeTimestamp(timestamp: number|string) {
        return +timestamp * 1000;
    }

    static getDay(date: Date): string {
        const day = date.getDate();
        return day < 10 ? `0${day}` : day.toString();
    }

    static getMonth(date: Date): string {
        let month = date.getMonth() + 1;
        if (month > 12) {
            month = 12;
        }

        if (month < 0) {
            month = 0;
        }

        return month < 10 ? `0${month}` : month.toString();
    }

    static getYear(date: Date): string {
        return date.getFullYear().toString();
    }

    static getTime(date: Date): string {
        const hours = date.getHours();
        const minutes = date.getMinutes();
        return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
    }

    static getTimeZoneOffset() {
        return (new Date()).getTimezoneOffset();
    }


    static today() {
        return new Date();
    }

    static addDays(date: Date, count: number = 0): Date {
        const time = date.getTime();
        const newTime = time + (count * this.oneDayTime);
        return new Date(newTime);
    }

    static addMonths(date: Date, count: number = 0): Date {
        return new Date(date.setMonth(date.getMonth() + count));
    }


    /**
     * Проверка, является ли this.momentum валидной датой
     * @param dateString
     * @returns {boolean}
     */
    public isValid(): boolean {
        /*
        * Все недозаполненные даты, у которых присутствуют символ '_' из маски ввода считаем их невалидными
        * */
        return this.dateString.indexOf('_') >= 0 ? false : this.moment.isValid();
    }

    /**
     * Прибавляем к текущей дате заданное число месяцев
     * @param month
     * @returns {DateService}
     */
    public addMonth(month): DateService {
        this.moment = this.moment.add(+month, 'months');
        return this;
    }

    /**
     * Прибавляем к текущей дате заданное число дней
     * @param day
     * @returns {DateService}
     */
    public addDay(day: number): DateService {
        this.moment = this.moment.add(day, 'days');
        return this;
    }

    /**
     * обновляет this.moment на значение переданной даты.
     * @param dateString
     * @returns {DateService}
     */
    public for(dateString): DateService {
        this.parse(dateString);
        return this;
    }

    /**
     * Парсит переданную строку в объект moment в заданном формате
     * @param dateString
     * @returns {moment.Moment}
     */
    private parse(dateString) {
        this.moment = moment(dateString, this.dateFormat, true);
    }
}
